import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';
import { CheckIcon } from '@heroicons/react/24/outline';

const WhatsappEmbeddedSignup = () => {
  const { apiCall } = useAuth();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedOnboardingType, setSelectedOnboardingType] = useState('whatsapp_business_onboarding');
  const [isProcessing, setIsProcessing] = useState(false);
  const [fbSdkLoaded, setFbSdkLoaded] = useState(false);
  const [successData, setSuccessData] = useState(null);

  // Meta configuration from env
  const appId = process.env.REACT_APP_META_APP_ID;
  const graphVersion = process.env.REACT_APP_META_GRAPH_VERSION || 'v21.0';
  
  const configIds = {
    whatsapp_business_onboarding: process.env.REACT_APP_META_CONFIG_ID_WABA_ONBOARDING,
    coexistence_mode: process.env.REACT_APP_META_CONFIG_ID_COEXISTENCE,
    waba_only_sharing: process.env.REACT_APP_META_CONFIG_ID_WABA_SHARING,
  };

  useEffect(() => {
    fetchUsers();
    loadFbSdk();

    // Message listener for FB Login popup
    const messageListener = (event) => {
      if (event.origin !== "https://www.facebook.com" && event.origin !== "https://web.facebook.com") return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'WA_EMBEDDED_SIGNUP') {
          // You could store session info here if needed before the callback completes
          console.log("Embedded Signup Event:", data);
        }
      } catch (e) {
        // Not a JSON message, ignore
      }
    };

    window.addEventListener('message', messageListener);
    return () => window.removeEventListener('message', messageListener);
  }, []);

  const fetchUsers = async () => {
    try {
      const data = await apiCall('/api/whatsapp-embedded/onboarding-list');
      if (data.success) {
        setUsers(data.users);
        if (data.users.length > 0) {
           setSelectedUser(data.users[0]._id);
        }
      }
    } catch (error) {
      toast.error('Failed to load users');
      console.error(error);
    }
  };

  const loadFbSdk = () => {
    if (document.getElementById('facebook-jssdk')) {
      setFbSdkLoaded(true);
      return;
    }

    window.fbAsyncInit = function() {
      window.FB.init({
        appId      : appId,
        cookie     : true,
        xfbml      : true,
        version    : graphVersion
      });
      setFbSdkLoaded(true);
    };

    (function(d, s, id){
       var js, fjs = d.getElementsByTagName(s)[0];
       if (d.getElementById(id)) {return;}
       js = d.createElement(s); js.id = id;
       js.src = "https://connect.facebook.net/en_US/sdk.js";
       fjs.parentNode.insertBefore(js, fjs);
     }(document, 'script', 'facebook-jssdk'));
  };

  const handleConnectClick = async () => {
    if (!selectedUser) {
      toast.error('Please select a user first');
      return;
    }

    if (!appId || !configIds[selectedOnboardingType]) {
      toast.error('Meta configuration missing for selected onboarding type.');
      return;
    }

    setIsProcessing(true);
    setSuccessData(null);

    try {
      // 1. Start session on backend
      await apiCall('/api/whatsapp-embedded/start', {
        method: 'POST',
        body: {
          userId: selectedUser,
          onboardingType: selectedOnboardingType
        }
      });

      // Prepare extras payload according to Meta Embedded Signup v3 specifications
      const extras = {
        setup: {
          version: 'v3'
        },
        sessionInfoVersion: '3',
        session_info_version: '3',
        'session info version': '3',
        'es-version': 'v3'
      };

      if (selectedOnboardingType === 'coexistence_mode') {
        extras.setup = {
          setup_mode: 'COEXISTENCE',
          version: 'v3'
        };
        extras.featureType = 'whatsapp_business_app_onboarding';
        extras.feature_type = 'whatsapp_business_app_onboarding';
        extras['Feature Type'] = 'whatsapp_business_app_onboarding';
        extras.features = ['marketing_messages_lite', 'app_only_install'];
        extras.sessionInfoVersion = '3';
        extras.session_info_version = '3';
        extras['session info version'] = '3';
        extras['es-version'] = 'v3';
      }

      // 2. Launch FB Login
      window.FB.login((response) => {
        if (response.authResponse && response.authResponse.code) {
          handleFbCallback(response.authResponse.code);
        } else {
          setIsProcessing(false);
          toast.error('Facebook login cancelled or failed');
        }
      }, {
        config_id: configIds[selectedOnboardingType],
        response_type: "code",
        override_default_response_type: true,
        extras: extras
      });
    } catch (error) {
      setIsProcessing(false);
      toast.error(error.message || 'Failed to start onboarding process');
      console.error(error);
    }
  };

  const handleFbCallback = async (code) => {
    try {
      toast.loading('Processing WhatsApp connection...', { id: 'wa-onboarding' });
      
      const data = await apiCall('/api/whatsapp-embedded/callback', {
        method: 'POST',
        body: {
          userId: selectedUser,
          onboardingType: selectedOnboardingType,
          authCode: code,
          sessionInfo: {} // Pass additional session info if captured from message event
        }
      });

      if (data.success) {
        toast.success('WhatsApp connected successfully!', { id: 'wa-onboarding' });
        setSuccessData(data.details);
      } else {
        toast.error('Failed to connect WhatsApp', { id: 'wa-onboarding' });
      }
    } catch (error) {
      toast.error(error.message || 'Error processing connection', { id: 'wa-onboarding' });
      console.error(error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6 font-sans">
      <div className="w-full mx-auto bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        
        {/* Header Section */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">1</div>
            <h1 className="text-2xl font-bold text-gray-800">Step 1: Select Onboarding Type</h1>
          </div>
          <p className="text-gray-600 ml-11">Choose how you want to connect WhatsApp.</p>
        </div>

        {/* User Selection */}
        <div className="p-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Connect For User</label>
          <select 
            className="w-full max-w-md p-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            disabled={isProcessing || users.length <= 1}
          >
            {users.length === 0 && <option value="">Loading users...</option>}
            {users.map(user => (
              <option key={user._id} value={user._id}>
                {user.name} {user.email ? `(${user.email})` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Success Card */}
        {successData && (
          <div className="px-6 mb-4">
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <h3 className="text-green-800 font-semibold mb-2">Connected Successfully!</h3>
              <ul className="text-green-700 text-sm space-y-1">
                <li><strong>WABA ID:</strong> {successData.wabaId || 'N/A'}</li>
                <li><strong>Phone Number ID:</strong> {successData.phoneNumberId || 'N/A'}</li>
                <li><strong>Business ID:</strong> {successData.businessId || 'N/A'}</li>
                <li><strong>Phone Number:</strong> {successData.phoneNumber || 'N/A'}</li>
              </ul>
              <button 
                className="mt-3 text-sm text-green-800 underline font-medium"
                onClick={() => setSuccessData(null)}
              >
                Connect another account
              </button>
            </div>
          </div>
        )}

        {/* Onboarding Cards Grid */}
        <div className="px-6 pb-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          
          {/* Card A: Business Onboarding */}
          <div 
            className={`border rounded-xl p-5 cursor-pointer transition-all ${
              selectedOnboardingType === 'whatsapp_business_onboarding' 
              ? 'border-blue-500 bg-blue-50 shadow-md ring-1 ring-blue-500' 
              : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => setSelectedOnboardingType('whatsapp_business_onboarding')}
          >
            <div className="w-10 h-10 rounded-md bg-emerald-500 text-white flex items-center justify-center font-bold mb-4">
              WA
            </div>
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-bold text-gray-900 text-lg">WhatsApp Business Onboarding</h3>
              <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-1 rounded">Recommended</span>
            </div>
            <p className="text-sm text-gray-600 mb-6 min-h-[40px]">
              Create a new WhatsApp Business Account with a new phone number
            </p>
            
            <div className="mb-6">
              <p className="text-sm font-semibold text-gray-700 mb-3">Features:</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <CheckIcon className="w-4 h-4 text-green-500 font-bold" /> WhatsApp Cloud API
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon className="w-4 h-4 text-green-500 font-bold" /> Marketing Messages Lite
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon className="w-4 h-4 text-green-500 font-bold" /> Templates
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon className="w-4 h-4 text-green-500 font-bold" /> Webhooks
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon className="w-4 h-4 text-green-500 font-bold" /> New Phone Number Setup
                </li>
              </ul>
            </div>
            
            <button 
              className={`w-full py-2.5 rounded-md font-semibold text-white transition-colors ${
                selectedOnboardingType === 'whatsapp_business_onboarding'
                ? 'bg-emerald-600 hover:bg-emerald-700'
                : 'bg-emerald-500 opacity-50'
              }`}
              onClick={handleConnectClick}
              disabled={isProcessing || selectedOnboardingType !== 'whatsapp_business_onboarding'}
            >
              {isProcessing && selectedOnboardingType === 'whatsapp_business_onboarding' ? 'Connecting...' : 'Connect WhatsApp'}
            </button>
          </div>

          {/* Card B: Coexistence Mode */}
          <div 
            className={`border rounded-xl p-5 cursor-pointer transition-all ${
              selectedOnboardingType === 'coexistence_mode' 
              ? 'border-blue-500 bg-blue-50 shadow-md ring-1 ring-blue-500' 
              : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => setSelectedOnboardingType('coexistence_mode')}
          >
            <div className="w-10 h-10 rounded-md bg-blue-500 text-white flex items-center justify-center font-bold mb-4">
              CO
            </div>
            <h3 className="font-bold text-gray-900 text-lg mb-2">Coexistence Mode</h3>
            <p className="text-sm text-gray-600 mb-6 min-h-[40px]">
              Connect existing WhatsApp Business App number
            </p>
            
            <div className="mb-6">
              <p className="text-sm font-semibold text-gray-700 mb-3">Features & Spec (v3):</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <CheckIcon className="w-4 h-4 text-green-500 font-bold" /> Embedded Signup v3
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon className="w-4 h-4 text-green-500 font-bold" /> Session Info Version: 3
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon className="w-4 h-4 text-green-500 font-bold" /> Marketing Messages Lite
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon className="w-4 h-4 text-green-500 font-bold" /> App Only Install
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon className="w-4 h-4 text-green-500 font-bold" /> WA Business App Onboarding
                </li>
              </ul>
            </div>
            
            <button 
              className={`w-full py-2.5 rounded-md font-semibold transition-colors mt-auto ${
                selectedOnboardingType === 'coexistence_mode'
                ? 'bg-white text-blue-600 border border-blue-600 hover:bg-blue-50'
                : 'bg-white text-gray-500 border border-gray-300'
              }`}
              onClick={handleConnectClick}
              disabled={isProcessing || selectedOnboardingType !== 'coexistence_mode'}
            >
              {isProcessing && selectedOnboardingType === 'coexistence_mode' ? 'Connecting...' : 'Connect Existing Number'}
            </button>
          </div>

          {/* Card C: WABA Only Sharing */}
          <div 
            className={`border rounded-xl p-5 cursor-pointer transition-all ${
              selectedOnboardingType === 'waba_only_sharing' 
              ? 'border-blue-500 bg-blue-50 shadow-md ring-1 ring-blue-500' 
              : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => setSelectedOnboardingType('waba_only_sharing')}
          >
            <div className="w-10 h-10 rounded-md bg-orange-500 text-white flex items-center justify-center font-bold mb-4">
              WB
            </div>
            <h3 className="font-bold text-gray-900 text-lg mb-2">WABA Only Sharing</h3>
            <p className="text-sm text-gray-600 mb-6 min-h-[40px]">
              Share only WhatsApp Business Account
            </p>
            
            <div className="mb-6">
              <p className="text-sm font-semibold text-gray-700 mb-3">Features:</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <CheckIcon className="w-4 h-4 text-green-500 font-bold" /> WABA Access
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon className="w-4 h-4 text-green-500 font-bold" /> No Phone Number Setup
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon className="w-4 h-4 text-green-500 font-bold" /> Business Asset Sharing
                </li>
              </ul>
            </div>
            
            <button 
              className={`w-full py-2.5 rounded-md font-semibold transition-colors mt-auto ${
                selectedOnboardingType === 'waba_only_sharing'
                ? 'bg-white text-blue-600 border border-blue-600 hover:bg-blue-50'
                : 'bg-white text-gray-500 border border-gray-300'
              }`}
              onClick={handleConnectClick}
              disabled={isProcessing || selectedOnboardingType !== 'waba_only_sharing'}
            >
              {isProcessing && selectedOnboardingType === 'waba_only_sharing' ? 'Connecting...' : 'Share WABA'}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default WhatsappEmbeddedSignup;
